import type { Payments } from 'core/domains';
import { put } from 'core/middlewares/utilities';
import { app, cart, checkout } from 'core/use-cases';
import { Utilities, CHECKOUT_VIEW } from 'core/domains';
import { history } from 'core/use-cases/utilities';

import { getters } from './getters';

/**
 * Responsible for verifying that the payment method credit card
 * has been selected and triggering the radio option.
 *
 */
function* isCreditCardOption(paymentCode: string) {
  const {
    data: { creditCard },
  }: getters.PaymentMethods = yield getters.getPaymentMethods();

  const paymentInfo = creditCard.paymentInfo.flatMap(({ info }) => info);

  const paymentCreditCard =
    paymentInfo?.find(({ paymentCode: code }) => Number(code) === Number(paymentCode)) || {};

  const creditCardOption = Object.keys(paymentCreditCard).length
    ? paymentCreditCard.paymentCode
    : creditCard?.paymentPlanType;

  if (Number(creditCardOption) === Number(paymentCode)) {
    yield put(
      checkout.actions.setCreditCardOption({
        paymentCode: creditCard?.paymentPlanType,
      }),
    );
  }
}

/**
 * Orchestrate the redirect to checkout page flow
 *
 */
export function* checkoutInit() {
  yield put(checkout.actions.setView(CHECKOUT_VIEW.PAYMENT_SELECTOR));
  yield put(checkout.actions.setStep('payment'));

  const { canSubmit, isRunning }: getters.Cart = yield getters.getCart();

  if (isRunning) {
    return;
  }

  if (canSubmit) {
    yield put(checkout.actions.setReady(canSubmit));

    const { status }: getters.PaymentMethods = yield getters.getPaymentMethods();

    if (status !== Utilities.STATUS.RUNNING) {
      yield put(checkout.actions.paymentMethodsFetch.request());
    }

    return;
  }

  const homePage: getters.RoutePath = yield getters.getHomepageRoute();

  history.push(homePage);
}

export function* checkoutRedirect() {
  yield put(app.actions.toggleCart(false));
  history.push('/checkout');
  yield put(cart.actions.cartFetch.request());
}

export function* giftsFlowCheck() {
  yield put(app.actions.toggleCart(false));
  yield put(cart.actions.cartFetchForGiftsCheck.request());
}

const findPayment = (paymentInfo: Payments['paymentInfo'], paymentCode: string) =>
  paymentInfo.find((item) => item.paymentCode === paymentCode) || {};

/**
 * Responsible for verifying that the payment method
 * has been selected and triggering the payment step action.
 *
 */
export function* isPaymentCanSubmit() {
  const { paymentCode }: getters.PaymentOption = yield getters.getPaymentOption();
  if (paymentCode) {
    const {
      data: { bankSlip, linkPayment, centralPayment },
    }: getters.PaymentMethods = yield getters.getPaymentMethods();

    const payment = findPayment(bankSlip.paymentInfo, paymentCode);

    if (Object.keys(payment).length) {
      yield put(checkout.actions.setBankSlipOption(paymentCode));

      return;
    }

    if (linkPayment) {
      const paymentLinkPayment = findPayment(linkPayment?.paymentInfo, paymentCode);

      if (Object.keys(paymentLinkPayment).length) {
        yield put(checkout.actions.setPayWithdrawalsOption(paymentCode));

        return;
      }
    }

    if (centralPayment) {
      const paymentFiltered = findPayment(centralPayment?.paymentInfo, paymentCode);

      if (Object.keys(paymentFiltered).length) {
        yield put(checkout.actions.setCentralPaymentOption(paymentCode));

        return;
      }
    }

    yield isCreditCardOption(paymentCode);
  }
}
